import { Modal } from "@arco-design/web-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { canUseResourcePoint } from "../service/permissions";
import { useCurrentAuthUser } from "../../auth";
import {
  resourcePointTypeOptions,
  scopeEntityLabel,
  scopeHidesTypeFilters,
  scopeLockedListFilters,
  type ResourcePointListScope,
} from "../config";
import { resourcePointSpuQueryRoot, useResourcePointManagementRuntime } from "../runtime";
import type { ResourcePointSkuItem, ResourcePointSpuItem } from "../types";
import { ResourcePointFilterPanel } from "./ResourcePointFilterPanel";
import {
  ResourcePointHeritageDetailDrawer,
  type ResourcePointHeritageDetailMode,
} from "./ResourcePointHeritageDetailDrawer";
import {
  ResourcePointHeritageUploadDrawer,
  type ResourcePointHeritageUploadState,
} from "./ResourcePointHeritageUploadDrawer";
import { ResourcePointSkuManagementDrawer } from "./ResourcePointSkuManagementDrawer";
import { ResourcePointSkuDrawer, type ResourcePointSkuDrawerState } from "./ResourcePointSkuDrawer";
import { ResourcePointSpuDrawer, type ResourcePointSpuDrawerState } from "./ResourcePointSpuDrawer";
import { ResourcePointSpuTable } from "./ResourcePointSpuTable";
import "./resourcePointManagement.css";

type DeleteState =
  | { type: "spu"; spu: ResourcePointSpuItem }
  | { type: "sku"; spu: ResourcePointSpuItem; sku: ResourcePointSkuItem };

type ResourcePointManagementFeaturePageProps = {
  scope?: ResourcePointListScope;
};

/** 考察点管理特性页主体。 */
export function ResourcePointManagementFeaturePage({
  scope = "all",
}: ResourcePointManagementFeaturePageProps) {
  const deepLinkSpuId = new URLSearchParams(window.location.search).get("geoEditId") ?? undefined;
  const runtime = useResourcePointManagementRuntime(deepLinkSpuId, scope);
  const queryClient = useQueryClient();
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUseResourcePoint(currentUser, scope, "manage");
  const canManageSpu = (spu: ResourcePointSpuItem) => canUseResourcePoint(currentUser, spu, "manage");
  const canDeleteSpu = (spu: ResourcePointSpuItem) => canUseResourcePoint(currentUser, spu, "delete");
  const [skuManagementSpu, setSkuManagementSpu] = useState<ResourcePointSpuItem | null>(null);
  const [spuDrawerState, setSpuDrawerState] = useState<ResourcePointSpuDrawerState | null>(null);
  const [skuDrawerState, setSkuDrawerState] = useState<ResourcePointSkuDrawerState | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState | null>(null);
  const [heritageUploadState, setHeritageUploadState] = useState<ResourcePointHeritageUploadState | null>(null);
  const [heritageDetailState, setHeritageDetailState] = useState<{
    spu: ResourcePointSpuItem;
    mode: ResourcePointHeritageDetailMode;
  } | null>(null);
  const spuDrawerStateRef = useRef<ResourcePointSpuDrawerState | null>(null);
  const spuDrawerTriggerRef = useRef<HTMLElement | null>(null);
  const skuManagementTriggerRef = useRef<HTMLElement | null>(null);
  const skuDrawerTriggerRef = useRef<HTMLElement | null>(null);
  const showHeritageActions = scope === "intangible";
  const showPointTypeColumn = !scopeHidesTypeFilters(scope);
  const entityLabel = scopeEntityLabel(scope);
  const openedDeepLinkRef = useRef<string | undefined>(undefined);
  const scopedTypeOptions = useMemo(() => {
    const lockedType = scopeLockedListFilters(scope).pointType;
    if (lockedType) return [lockedType];
    return resourcePointTypeOptions.map((item) => item.value);
  }, [scope]);
  const pointTypes = useMemo(
    () => Array.from(new Set([
      ...scopedTypeOptions,
      ...runtime.items.map((item) => item.pointType).filter(Boolean),
    ])),
    [runtime.items, scopedTypeOptions],
  );

  useEffect(() => {
    if (!deepLinkSpuId || !runtime.deepLinkedSpu || !canUseResourcePoint(currentUser, runtime.deepLinkedSpu, "manage")) return;
    if (openedDeepLinkRef.current === deepLinkSpuId) return;
    openedDeepLinkRef.current = deepLinkSpuId;
    runtime.clearMutationError();
    runtime.clearGeoResult();
    spuDrawerStateRef.current = { type: "edit", spu: runtime.deepLinkedSpu };
    setSpuDrawerState({ type: "edit", spu: runtime.deepLinkedSpu });
  }, [deepLinkSpuId, runtime, currentUser]);

  /** 从列表 GEO 标签识别：已确认直接保存，其余打开编辑抽屉。 */
  async function handleListVerifyGeo(spu: ResourcePointSpuItem) {
    if (!canManageSpu(spu) || runtime.geoPending) return;
    const outcome = await runtime.handleVerifyListSpuGeo(spu);
    if (!("result" in outcome) || outcome.saved) return;
    spuDrawerTriggerRef.current = activeElement();
    spuDrawerStateRef.current = { type: "edit", spu };
    setSpuDrawerState({ type: "edit", spu });
  }

  /** 确认当前逻辑删除操作。 */
  async function confirmDelete() {
    if (!deleteState || !canDeleteSpu(deleteState.spu) || runtime.mutationPending) return;
    const success = deleteState.type === "spu"
      ? await runtime.handleDeleteSpu(deleteState.spu.resourcePointSpuId)
      : await runtime.handleDeleteSku(
        deleteState.spu.resourcePointSpuId,
        deleteState.sku.resourcePointSkuId,
      );
    if (!success) return;
    if (deleteState.type === "spu") {
      setSkuManagementSpu((current) =>
        current?.resourcePointSpuId === deleteState.spu.resourcePointSpuId ? null : current,
      );
    }
    setDeleteState(null);
  }

  return (
    <main className="resource-point-page">
      <ResourcePointFilterPanel
        filters={runtime.draftFilters}
        fetching={runtime.fetching}
        scope={scope}
        onChange={runtime.setDraftFilters}
        onReset={runtime.handleReset}
        onSearch={runtime.handleSearch}
      />
      <ResourcePointSpuTable
        items={runtime.items}
        loading={runtime.loading}
        errorMessage={runtime.errorMessage || (!spuDrawerState ? runtime.mutationErrorMessage : "")}
        page={runtime.page}
        pageSize={runtime.pageSize}
        total={runtime.total}
        canManage={canManage}
        canManageSpu={canManageSpu}
        canDeleteSpu={canDeleteSpu}
        entityLabel={entityLabel}
        showHeritageActions={showHeritageActions}
        showPointTypeColumn={showPointTypeColumn}
        geoPending={runtime.geoPending}
        geoVerifyingSpuId={runtime.geoVerifyingSpuId}
        onCreateSpu={() => {
          spuDrawerTriggerRef.current = activeElement();
          runtime.clearMutationError();
          runtime.clearGeoResult();
          const next = { type: "create" as const };
          spuDrawerStateRef.current = next;
          setSpuDrawerState(next);
        }}
        onEditSpu={(spu) => {
          spuDrawerTriggerRef.current = activeElement();
          runtime.clearMutationError();
          runtime.clearGeoResult();
          const next = { type: "edit" as const, spu };
          spuDrawerStateRef.current = next;
          setSpuDrawerState(next);
        }}
        onDeleteSpu={(spu) => {
          runtime.clearMutationError();
          setDeleteState({ type: "spu", spu });
        }}
        onManageSku={(spu) => {
          skuManagementTriggerRef.current = activeElement();
          runtime.clearMutationError();
          runtime.clearSkuToggleError(spu.resourcePointSpuId);
          setSkuManagementSpu(spu);
        }}
        onVerifyGeo={(spu) => {
          void handleListVerifyGeo(spu);
        }}
        onViewHeritageDetail={(spu) => {
          runtime.clearMutationError();
          setHeritageDetailState({ spu, mode: "detail" });
        }}
        onViewHeritageGallery={(spu, mode) => {
          runtime.clearMutationError();
          setHeritageDetailState({ spu, mode });
        }}
        sortField={runtime.sortField}
        sortDirection={runtime.sortDirection}
        onSort={runtime.handleSort}
        onPageChange={runtime.handlePageChange}
        onPageSizeChange={runtime.handlePageSizeChange}
      />
      <ResourcePointSpuDrawer
        state={spuDrawerState}
        scope={scope}
        pointTypes={pointTypes}
        pending={runtime.mutationPending}
        errorMessage={runtime.mutationErrorMessage}
        geoPending={runtime.geoPending}
        geoStatus={runtime.geoStatus}
        geoResult={runtime.geoResult}
        geoOwnerSpuId={runtime.geoOwnerSpuId}
        onClearError={runtime.clearMutationError}
        onHydrateGeo={runtime.hydrateSavedGeo}
        onVerifyGeo={runtime.handleVerifySpuGeo}
        onAdoptGeo={runtime.handleAdoptGeoCandidate}
        onClose={() => {
          spuDrawerStateRef.current = null;
          setSpuDrawerState(null);
        }}
        onAfterClose={() => {
          if (!spuDrawerStateRef.current) {
            runtime.clearGeoResult();
          }
          spuDrawerTriggerRef.current?.focus();
        }}
        onCreate={runtime.handleCreateSpu}
        onUpdate={runtime.handleUpdateSpu}
      />
      <ResourcePointSkuManagementDrawer
        spu={skuManagementSpu}
        canManage={canUseResourcePoint(currentUser, skuManagementSpu, "manage")}
        canDelete={canUseResourcePoint(currentUser, skuManagementSpu, "delete")}
        inactive={Boolean(skuDrawerState)}
        onClose={() => {
          if (skuManagementSpu) {
            runtime.clearSkuToggleError(skuManagementSpu.resourcePointSpuId);
          }
          setSkuManagementSpu(null);
        }}
        onAfterClose={() => skuManagementTriggerRef.current?.focus()}
        onCreate={(spu) => {
          skuDrawerTriggerRef.current = activeElement();
          runtime.clearMutationError();
          setSkuDrawerState({ type: "create", spu });
        }}
        onEdit={(spu, sku) => {
          skuDrawerTriggerRef.current = activeElement();
          runtime.clearMutationError();
          setSkuDrawerState({ type: "edit", spu, sku });
        }}
        onDelete={(spu, sku) => {
          runtime.clearMutationError();
          setDeleteState({ type: "sku", spu, sku });
        }}
        onToggleDisabled={(spu, sku) => runtime.handleToggleSkuDisabled(spu.resourcePointSpuId, sku)}
        togglePendingSkuIds={runtime.togglePendingSkuIds}
        errorMessage={skuManagementSpu
          ? runtime.skuToggleErrorMessages[skuManagementSpu.resourcePointSpuId] ?? ""
          : ""}
      />
      <ResourcePointSkuDrawer
        state={skuDrawerState}
        pending={runtime.mutationPending}
        errorMessage={runtime.mutationErrorMessage}
        onClearError={runtime.clearMutationError}
        onClose={() => setSkuDrawerState(null)}
        onAfterClose={() => skuDrawerTriggerRef.current?.focus()}
        onCreate={runtime.handleCreateSku}
        onUpdate={runtime.handleUpdateSku}
      />
      <ResourcePointHeritageDetailDrawer
        spu={heritageDetailState?.spu ?? null}
        mode={heritageDetailState?.mode ?? "detail"}
        canManage={canUseResourcePoint(currentUser, heritageDetailState?.spu ?? null, "manage")}
        canDelete={canUseResourcePoint(currentUser, heritageDetailState?.spu ?? null, "delete")}
        onClose={() => {
          if (heritageUploadState) return;
          setHeritageDetailState(null);
        }}
        onUpload={(spu, lockedScene, existingItem) => {
          if (existingItem) {
            setHeritageUploadState({
              type: "edit",
              spu,
              item: existingItem,
            });
            return;
          }
          setHeritageUploadState({
            type: "create",
            spu,
            lockedScene,
          });
        }}
      />
      <ResourcePointHeritageUploadDrawer
        state={heritageUploadState}
        onClose={() => setHeritageUploadState(null)}
        onSaved={() => {
          if (!heritageUploadState) return;
          void queryClient.invalidateQueries({
            queryKey: ["resource-point-management", "media", heritageUploadState.spu.resourcePointSpuId],
          });
          void queryClient.invalidateQueries({ queryKey: resourcePointSpuQueryRoot });
        }}
      />
      <Modal
        className="resource-point-delete-modal"
        title={deleteState?.type === "sku" ? "删除 SKU" : "删除考察点"}
        visible={Boolean(deleteState)}
        unmountOnExit
        confirmLoading={runtime.mutationPending}
        okButtonProps={{ status: "danger", disabled: runtime.mutationPending }}
        okText={runtime.mutationPending ? "删除中..." : "确认删除"}
        cancelText="取消"
        onOk={confirmDelete}
        onCancel={() => {
          if (runtime.mutationPending) return;
          runtime.clearMutationError();
          setDeleteState(null);
        }}
      >
        {runtime.mutationErrorMessage ? (
          <div className="resource-point-error" role="alert">{runtime.mutationErrorMessage}</div>
        ) : null}
        {deleteState?.type === "spu" ? (
          <div className="resource-point-delete-summary">
            <p>确定删除考察点「<strong>{deleteState.spu.pointName}</strong>」吗？</p>
            <p>SKU 数：{deleteState.spu.skuCount}</p>
            <p className="resource-point-delete-warning">
              删除后将停用并保留记录，该考察点及其全部 SKU 可通过编辑重新启用。
            </p>
          </div>
        ) : null}
        {deleteState?.type === "sku" ? (
          <div className="resource-point-delete-summary">
            <p>确定删除 SKU「<strong>{deleteState.sku.skuName}</strong>」吗？</p>
            <p>所属考察点：{deleteState.spu.pointName}</p>
            <p className="resource-point-delete-warning">
              删除后将停用并保留记录，该 SKU 可通过编辑重新启用。
            </p>
          </div>
        ) : null}
      </Modal>
    </main>
  );
}

/** 返回当前可恢复焦点的活动元素。 */
function activeElement(): HTMLElement | null {
  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}
